import { getDeviceCommonName, isModelVirtualWeatherStation } from '@weenat/client/dist/core/devices'
import { isDevice, isPlot, isStation } from '@weenat/client/dist/core/utils/resourcesTypeCheckers'
import { useGetResourcesByRoute } from '@weenat/client/dist/hooks'
import { Device } from '@weenat/client/dist/resources/devices'
import { Plot } from '@weenat/client/dist/resources/plots'
import { Station } from '@weenat/client/dist/resources/stations'
import { Id } from '@weenat/client/dist/resources/types'
import { useIntl } from '@weenat/wintl'
import { useOrgContext } from 'app/orgProvider'
import { useNavigate, usePathname } from 'app/routx-router'
import { useRecentlyOpenedElements } from 'app/state'
import { isEmpty, isNil } from 'lodash-es'
import { FC, useCallback, useMemo, useRef, useState } from 'react'
import { useClickAway } from 'react-use'
import { styled } from 'styled-components'
import NoSummariesMatchingSearchTermContent from '../dashboard/components/DashboardMap/NoSummariesMatchingSearchTermContent'
import useDisclosure from '../hooks/useDisclosure'
import InvisibleTextInput from '../kit/fields/InvisibleInput'
import { Dropdown, DropdownContent, DropdownTrigger } from '../kit/fields/Select/SelectDropdown'
import Icons from '../kit/Icons'
import LoadingCircle from '../kit/loaders/LoadingCircle'
import PanelSectionTitle from '../kit/panels/PanelSectionTitle'
import Text from '../kit/Text'
import { SearchResult } from '../myFarm/tools/SearchResult'

const resourcePathRegex = /(\/farms\/\d*\/(plots|stations)\/\d*\/)/gm

const Interactive = styled(Flex)`
  position: relative;

  cursor: pointer;
  width: fit-content;

  &::after {
    content: '';
    position: absolute;
    background-color: transparent;
    inset: -8px;
    border-radius: ${(p) => p.theme.radiuses.md}px;
    opacity: 0.1;
  }

  &:hover::after,
  &.active::after {
    background-color: ${(p) => p.theme.colors.primary[500]};
  }
`

interface TitleTextProps {
  title: string
}

export const TitleText: FC<TitleTextProps> = ({ title }) => {
  return (
    <Text $fontSize='md' $lineHeight='lg' $fontWeight='semiBold' $ellipsis>
      {title}
    </Text>
  )
}

interface ResourceSwitcherProps {
  title: string
  organizationId: Id
  isWeatherMap: boolean
  enabled: boolean
}

const ResourceSwitcher: FC<ResourceSwitcherProps> = ({
  title,
  organizationId,
  isWeatherMap,
  enabled
}) => {
  const org = useOrgContext()
  const pathname = usePathname()
  const nav = useNavigate()
  const { t } = useIntl()

  const [searchQuery, setSearchQuery] = useState<string>('')
  const interactiveRef = useRef<HTMLDivElement>(null)
  const elementDropdown = useDisclosure()

  const [recentlyOpenedElements] = useRecentlyOpenedElements()

  const { items, isLoading } = useGetResourcesByRoute({
    organizationId,
    isWeatherMap,
    enabled,
    searchQuery: isEmpty(searchQuery) ? undefined : searchQuery
  })

  const segmentsAfterResourcePath = useMemo(() => {
    const match = pathname.match(resourcePathRegex)
    return !isNil(match) ? pathname.replace(match[0], '') : ''
  }, [pathname])

  const closeDropdown = useCallback(() => {
    elementDropdown.close()
  }, [elementDropdown])

  // Determine what resource kinds are present in the list to filter recent items
  const itemKinds = useMemo(() => {
    const kinds = new Set<'plot' | 'station' | 'device'>()
    items?.forEach((i: Plot | Station | Device) => {
      if (isDevice(i)) kinds.add('device')
      else if (isPlot(i)) kinds.add('plot')
      else kinds.add('station')
    })
    return kinds
  }, [items])

  const relevantRecentItems = useMemo(
    () =>
      recentlyOpenedElements.filter((el) => el.organizationId === org.id && itemKinds.has(el.kind)),
    [recentlyOpenedElements, org.id, itemKinds]
  )

  // Keys of recently opened items so they can be excluded from the main list
  // to avoid showing the same resource twice when there is no active search.
  const recentItemKeys = useMemo(
    () => new Set(relevantRecentItems.map((el) => `${el.kind}-${el.id}`)),
    [relevantRecentItems]
  )

  const deduplicatedItems = useMemo(
    () =>
      isEmpty(searchQuery)
        ? items?.filter((item: Plot | Station | Device) => {
            const isItemADevice = isDevice(item)
            const isItemAPlot = isPlot(item)
            const kind = isItemADevice ? 'device' : isItemAPlot ? 'plot' : 'station'
            return !recentItemKeys.has(`${kind}-${item.id}`)
          })
        : items,
    [items, searchQuery, recentItemKeys]
  )

  // Compute the navigation target for a summary, preserving the current sub-path
  const getSummaryNavTarget = useCallback(
    (item: Plot | Station | Device): string => {
      const pathToResource = isPlot(item)
        ? `/farms/${org.id}/plots/${item.id}`
        : isStation(item)
          ? `/farms/${org.id}/stations/${item.id}`
          : `/weather-map/${item.id}`

      const preserveSegments =
        isPlot(item) ||
        segmentsAfterResourcePath.startsWith('forecasts') ||
        segmentsAfterResourcePath.startsWith('history')

      return preserveSegments ? `${pathToResource}/${segmentsAfterResourcePath}` : pathToResource
    },
    [org.id, segmentsAfterResourcePath]
  )

  useClickAway(interactiveRef, () => {
    // I don't know why it captures the parent even though the ref is on the correct element
    const target = interactiveRef?.current?.firstChild
    const c = target?.className as string

    if (!isNil(c) && c.includes('active') && !isNil(target)) {
      target.className = c.replace('active', '').trimEnd()
    }
  })

  return (
    <Dropdown
      isVisible={elementDropdown.isOpen}
      onVisibleChange={elementDropdown.toggle}
      placement='bottom-start'
      role='listbox'
      marginFromParent={16}
    >
      <DropdownTrigger>
        <Interactive
          ref={interactiveRef}
          onClick={(e) => {
            e.currentTarget.setAttribute('class', `${e.currentTarget.className} active`)
            elementDropdown.open()
          }}
        >
          <TitleText title={title} />
          <Icons.Unfold $size='lg' />
        </Interactive>
      </DropdownTrigger>
      <DropdownContent
        width={640}
        Header={
          <Flex $backgroundColor='grayscale.100' $p='md' $alignItems='center'>
            <Icons.Search $size='md' />
            <InvisibleTextInput
              shouldFocusOnMount
              id='search-elements-dropdown'
              name='search-elements-dropdown'
              autoComplete='on'
              autoCorrect='off'
              type='text'
              value={searchQuery}
              onChange={(e) => {
                if (!isNil(e.currentTarget.value)) setSearchQuery(e.currentTarget.value)
              }}
              placeholder={t('actions.search', { capitalize: true })}
            />
          </Flex>
        }
      >
        <Flex $flexDirection='column' $gap={'md'} $p='md'>
          {/* Recently opened section — only when there is no active search */}
          {isEmpty(searchQuery) && !isEmpty(relevantRecentItems) ? (
            <Flex $flexDirection='column' $py={'sm'}>
              <PanelSectionTitle $mb='sm'>{t('filtering.previously_opened')}</PanelSectionTitle>
              <Box $pt='sm'>
                {relevantRecentItems.map((el) => (
                  <SearchResult
                    key={`recent-${el.kind}-${el.id}`}
                    id={el.id}
                    name={el.name}
                    kind={el.kind}
                    organizationId={el.organizationId}
                    reverseGeocoding={el.reverseGeocoding}
                    isFavorite={el.isFavorite}
                    technicalName={el.technicalName}
                    isVirtual={el.isVirtual}
                    onPress={closeDropdown}
                    hideActionLabel
                  />
                ))}
              </Box>
            </Flex>
          ) : null}

          {/* Main list: loading skeleton → items → empty state */}
          <Flex $flexDirection='column' $py={'sm'}>
            <PanelSectionTitle $mb='sm'>
              {!isEmpty(searchQuery)
                ? t('filtering.results', { capitalize: true })
                : isWeatherMap
                  ? t('models.device.model.name', { capitalize: true })
                  : org.name}
            </PanelSectionTitle>

            {isLoading ? (
              <LoadingCircle />
            ) : !isEmpty(deduplicatedItems) ? (
              deduplicatedItems.map((item: Plot | Station | Device) => {
                const isItemAPlot = isPlot(item)
                const isItemADevice = isDevice(item)

                const { id, reverseGeocoding } = item

                const name = isItemADevice ? getDeviceCommonName(item) : item.name

                const technicalName = !isItemAPlot ? item.endUserName.join(' ') : undefined

                const isFavorite = isItemADevice ? undefined : item.isFavorite

                let isVirtual = false

                if (!isItemAPlot) {
                  isVirtual = isModelVirtualWeatherStation(item.model)
                }

                const kind = isItemADevice
                  ? ('device' as const)
                  : isItemAPlot
                    ? ('plot' as const)
                    : ('station' as const)

                const to = getSummaryNavTarget(item)

                const key = `${kind}-${id}`

                return (
                  <SearchResult
                    key={key}
                    id={id}
                    name={name}
                    technicalName={technicalName}
                    reverseGeocoding={reverseGeocoding}
                    isFavorite={isFavorite}
                    isVirtual={isVirtual}
                    kind={kind}
                    organizationId={org.id}
                    onNavigate={() => {
                      nav(to)
                      closeDropdown()
                    }}
                    hideActionLabel
                  />
                )
              })
            ) : !isEmpty(searchQuery) ? (
              <NoSummariesMatchingSearchTermContent searchTerm={searchQuery} />
            ) : null}
          </Flex>
        </Flex>
      </DropdownContent>
    </Dropdown>
  )
}

export default ResourceSwitcher
